// Respaldo del sistema: un Excel con una hoja por tabla, para guardar en la carpeta privada. Funciones puras, con pruebas.
// La lista de tablas debe tener TODAS las de la base (menos `equipo`, que se administra por migración): una prueba del
// recorrido de punta a punta lo revisa, para que una tabla nueva no quede fuera del respaldo sin que nadie se entere.
import { diasEntre } from './fechas';
import type { Celda, Hoja } from './xlsx';

// t = tabla; o = columnas con las que se ordena para leerla por páginas sin repetir ni saltarse filas.
export const TABLAS_RESPALDO: { t: string; o: string[] }[] = [
  { t: 'parametros', o: ['clave'] },
  { t: 'monedas', o: ['codigo'] },
  { t: 'canales', o: ['id'] },
  { t: 'familias', o: ['id'] },
  { t: 'categorias', o: ['id'] },
  { t: 'colores_tela', o: ['id'] },
  { t: 'cuentas_bancarias', o: ['id'] },
  { t: 'tallas', o: ['grupo', 'talla'] },
  { t: 'productos', o: ['sku'] },
  { t: 'precios', o: ['sku', 'moneda'] },
  { t: 'materiales', o: ['codigo'] },
  { t: 'insumos_producto', o: ['sku', 'codigo'] },
  { t: 'proveedores', o: ['id'] },
  { t: 'compras', o: ['id'] },
  { t: 'clientes', o: ['id'] },
  { t: 'compras_historicas', o: ['id'] },
  { t: 'pedidos', o: ['id'] },
  { t: 'pedido_lineas', o: ['id'] },
  { t: 'pagos', o: ['id'] },
  { t: 'pagos_declarados', o: ['id'] },
  { t: 'enlaces_pedido', o: ['token'] },
  { t: 'archivos', o: ['id'] },
  { t: 'gastos', o: ['id'] },
  { t: 'campanas_pauta', o: ['id'] },
  { t: 'anuncios', o: ['id'] },
  { t: 'meta_resultados', o: ['id'] },
  { t: 'propuestas', o: ['id'] },
  { t: 'segmentos', o: ['id'] },
  { t: 'campanas', o: ['id'] },
  { t: 'campana_destinatarios', o: ['campana_id', 'cliente_id'] },
  { t: 'resultados_historicos', o: ['anio', 'mes', 'concepto'] },
  { t: 'todo_hojas', o: ['id'] },
  { t: 'todo_pendientes', o: ['id'] },
];
// Tablas de la base que no van en el respaldo, con la razón.
export const SIN_RESPALDO: Record<string, string> = { equipo: 'quién puede entrar: se administra con una migración' };

// Un valor de la base a una celda de Excel. Se guarda tal cual, para poder reconstruir: sí/no como true/false y las
// listas y objetos como JSON.
export function celdaRespaldo(v: unknown): Celda {
  if (v == null) return null;
  if (typeof v === 'number' || typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return JSON.stringify(v);
}
// Una celda de Excel admite 32.767 caracteres. Un texto más largo (los dibujos de una hoja del bloc de To Do's) se parte en columnas
// seguidas: `trazos`, `trazos__2`, `trazos__3`… Unidas en orden dan el valor original, así que el respaldo no pierde nada.
export const MAX_CELDA = 32000;
export function hojaDeTabla(nombre: string, filas: Record<string, unknown>[]): Hoja {
  return { nombre, filas: filas.map(f => Object.fromEntries(Object.entries(f).flatMap(([k, v]) => {
    const c = celdaRespaldo(v);
    if (typeof c !== 'string' || c.length <= MAX_CELDA) return [[k, c]];
    return Array.from({ length: Math.ceil(c.length / MAX_CELDA) }, (_, i) => [i ? `${k}__${i + 1}` : k, c.slice(i * MAX_CELDA, (i + 1) * MAX_CELDA)]);
  }))) };
}

// Primera hoja: qué es el archivo y cuántas filas trae cada hoja (sirve para comprobar que no falta nada).
export function hojaLeeme(fecha: string, ambiente: string, conteos: [string, number][]): Hoja {
  const nota = (hoja: string, texto: string) => ({ Hoja: hoja, Filas: null, Nota: texto });
  return { nombre: 'LEEME', filas: [
    nota('Respaldo del sistema Talentino', `Generado el ${fecha}. Ambiente: ${ambiente}.`),
    nota('Datos personales', 'Trae nombres y teléfonos de clientes y datos de cuentas bancarias: guárdalo en la carpeta privada, no lo compartas ni lo subas al repositorio.'),
    nota('Fotos', 'No trae las fotos de los dibujos: están en el depósito de imágenes. Las hojas traen sus rutas.'),
    nota('Formato', 'Una hoja por tabla, con los datos tal cual están en la base (sí/no como true/false; listas como JSON).'),
    nota('', ''),
    ...conteos.map(([t, n]) => ({ Hoja: t, Filas: n, Nota: n ? '' : 'Vacía' })),
  ] };
}

// Cuántos días van desde el último respaldo y si ya toca otro (cada mes, según la guía de operación).
export const DIAS_ENTRE_RESPALDOS = 31;
export function estadoRespaldo(ultimo: string | null, hoy: string, hayDatos: boolean): { dias: number | null; toca: boolean } {
  const dias = ultimo ? diasEntre(ultimo, hoy) : null;
  return { dias, toca: hayDatos && (dias == null || dias > DIAS_ENTRE_RESPALDOS) };
}
export const nombreArchivoRespaldo = (ambiente: string, fecha: string) => `respaldo-talentino${ambiente === 'pruebas' ? '-pruebas' : ''}-${fecha}.xlsx`;
